import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
}

/**
 * Limite de error de la aplicacion.
 *
 * Sin un limite como este, cualquier excepcion no capturada durante el render, dentro de un
 * efecto o en la limpieza de un efecto hace que React 18 desmonte la raiz completa: el usuario
 * queda con la pantalla vacia y la aplicacion deja de responder, sin forma de recuperarse.
 *
 * Con el limite el fallo queda contenido y se ofrece recargar. Cuando no hay errores el
 * componente renderiza children sin modificar nada, por lo que no altera el comportamiento
 * existente de la aplicacion.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Se mantiene el registro en consola para que el fallo siga siendo diagnosticable.
        console.error("Error no capturado en la interfaz:", error, errorInfo.componentStack);
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div
                role="alert"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    minHeight: "100vh",
                    padding: "24px",
                    textAlign: "center",
                    fontFamily: "Poppins, sans-serif",
                    backgroundColor: "#ffffff",
                    color: "#333333"
                }}
            >
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>Ocurrio un error inesperado</h2>
                <p style={{ margin: 0, maxWidth: "480px", lineHeight: 1.5 }}>
                    No fue posible mostrar la conversacion. Recarga la pagina para continuar; si el problema persiste, comunicalo al area de TI.
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        padding: "10px 24px",
                        border: "none",
                        borderRadius: "6px",
                        backgroundColor: "#2f9f91",
                        color: "#ffffff",
                        fontSize: "15px",
                        fontWeight: 500,
                        cursor: "pointer"
                    }}
                >
                    Recargar
                </button>
            </div>
        );
    }
}
